import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DespachoInfoCallout,
  DespachoSectionCard,
  DespachoSimpleTable,
} from "@/components/despacho/DespachoUi";
import {
  despachoFiscalInfoApi,
  parseFiscalInfoError,
} from "@/modules/despacho/despachoFiscalInfoApi";
import { filingTypeLabel } from "@/modules/despacho/complianceInstanceHelpers";
import { formatIsoDateMx } from "@/lib/dateMx";
import ComplianceInstanceDetailDialog from "@/pages/despacho/ComplianceInstanceDetailDialog";

function isFollowupSent(row) {
  return Boolean(row?.client_followup_sent) || row?.client_reminder_status === "sent";
}

/**
 * Listado de vencimientos con seguimiento ya marcado como enviado al cliente.
 * Antes vivía en Tareas → Fiscales; se centraliza en Recordatorios a clientes.
 */
export default function FollowupSentPanel({ readOnly = false }) {
  const [items, setItems] = useState([]);
  const [loadState, setLoadState] = useState("loading");
  const [detailId, setDetailId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const load = useCallback(async () => {
    setLoadState("loading");
    try {
      const rows = await despachoFiscalInfoApi.listComplianceInstances({});
      setItems((rows || []).filter(isFollowupSent));
      setLoadState("ready");
    } catch (err) {
      setItems([]);
      setLoadState("error");
      toast.error(parseFiscalInfoError(err).message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo(
    () => [
      { key: "client_name", label: "Cliente", render: (r) => r.client_name || r.client_rfc || "—" },
      { key: "obligation_name", label: "Obligación", render: (r) => r.obligation_name || "—" },
      { key: "period", label: "Periodo", render: (r) => r.period || r.period_key || "—" },
      {
        key: "filing_type_label",
        label: "Tipo",
        render: (r) => r.filing_type_label || filingTypeLabel(r.filing_type),
      },
      {
        key: "client_followup_sent_at",
        label: "Enviado",
        render: (r) => formatIsoDateMx(r.client_followup_sent_at) || "✓",
      },
      {
        key: "client_reminder_label",
        label: "Recordatorio",
        render: (r) => r.client_reminder_label || "—",
      },
      {
        key: "_open",
        label: "",
        render: (r) => (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-7 px-0 text-xs"
            onClick={() => {
              setDetailId(r.id);
              setDetailOpen(true);
            }}
          >
            Detalle
          </Button>
        ),
      },
    ],
    [],
  );

  if (loadState === "loading") {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-600 py-6" data-testid="followup-sent-loading">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Cargando seguimientos enviados…
      </div>
    );
  }

  if (loadState === "error") {
    return (
      <DespachoSectionCard title="Seguimiento enviado">
        <p className="text-sm text-rose-800 m-0 mb-2">No se pudo cargar el listado.</p>
        <Button type="button" size="sm" variant="outline" className="rounded-sm" onClick={load}>
          Reintentar
        </Button>
      </DespachoSectionCard>
    );
  }

  return (
    <div className="space-y-4" data-testid="followup-sent-panel">
      <DespachoInfoCallout>
        Obligaciones donde ya se marcó <strong>seguimiento enviado al cliente</strong> (casilla en captura
        mensual o detalle del vencimiento). Fuente canónica: campos de la instancia fiscal
        (<code className="text-[10px]">client_followup_sent</code>). La bandeja de borradores/mensajes es otra
        vista (misma sección → Bandeja de envío); si el borrador está ligado a una obligación y se marca
        «Enviado», el sistema también actualiza esta marca.
      </DespachoInfoCallout>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-slate-600 m-0">{items.length} vencimiento(s) con seguimiento enviado</p>
        <Button type="button" size="sm" variant="ghost" className="rounded-sm h-8" onClick={load}>
          <RefreshCw className="h-3.5 w-3.5 mr-1" aria-hidden />
          Refrescar
        </Button>
      </div>

      <DespachoSectionCard
        title="Seguimiento enviado"
        description="Filtro de instancias con client_followup_sent o recordatorio marcado como enviado."
      >
        {items.length ? (
          <DespachoSimpleTable columns={columns} rows={items} emptyLabel="Sin registros." />
        ) : (
          <p className="text-sm text-slate-600 m-0">
            Aún no hay seguimientos marcados como enviados. Regístrelos en la captura mensual o en el detalle
            del vencimiento (casilla «Seguimiento enviado al cliente»).
          </p>
        )}
      </DespachoSectionCard>

      <ComplianceInstanceDetailDialog
        instanceId={detailId}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onUpdated={load}
        readOnly={readOnly}
      />
    </div>
  );
}
